import React from "react";
import Image from "next/image";
import Link from "next/link";
import { INTRO_CALL_URL } from "@/constants/homepage";

const COOKIE_SETTINGS = "#cookie-settings";
const linkClass =
  "inline-block rounded-sm py-1.5 text-sm leading-5 text-[#737373] transition-colors hover:text-[#231617] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#2B1B1C]";

const FOOTER_GROUPS = [
  {
    title: "Hiring",
    links: [
      { label: "Hire designers", href: "/" },
      { label: "How it works", href: "/#how-it-works" },
      { label: "Pricing", href: "/#pricing" },
      { label: "Book a call", href: INTRO_CALL_URL },
    ],
  },
  {
    title: "Designers",
    links: [
      { label: "Apply now", href: "/join" },
      { label: "Portfolio review", href: "/portfolio-review" },
      { label: "Events", href: "/events" },
    ],
  },
  {
    title: "Resources",
    links: [
      { label: "Blog", href: "/blog" },
      { label: "Talent report", href: "/report" },
      { label: "Design talent data", href: "https://data.world-class.design/" },
      {
        label: "Salary data",
        href: "https://data.world-class.design/?mode=salaries",
      },
      {
        label: "Podcast",
        href: "https://open.spotify.com/show/7mIQBp2BlpLt8rVenjJhSd",
      },
      { label: "YouTube", href: "https://www.youtube.com/@world-class-design" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "About us", href: "/about" },
      { label: "Sponsor an event", href: "/events#sponsor" },
      { label: "Contact", href: "mailto:guidione@world-class.design" },
      { label: "LinkedIn", href: "https://www.linkedin.com/company/wrlcd/" },
    ],
  },
  {
    title: "Legal",
    links: [
      { label: "Terms of service", href: "/terms" },
      { label: "Privacy policy", href: "/privacy" },
      { label: "Cookie policy", href: "/cookies" },
      // Opens the CookieYes preference centre (replaces its floating revisit button).
      { label: "Cookie settings", href: COOKIE_SETTINGS },
    ],
  },
];

const SiteFooter: React.FC = () => (
  <footer
    id="footer"
    className="scroll-mt-20 bg-white pb-10 pt-10 lg:pb-16 lg:pt-14"
  >
    <div className="mx-auto w-full max-w-[1168px] px-5 md:px-8">
      <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,3fr)] lg:gap-12 xl:grid-cols-[minmax(0,1fr)_minmax(0,4fr)] xl:gap-12">
        <div className="order-last flex flex-row items-center justify-between border-t border-dashed border-[#e5e5e5] pt-8 lg:order-none lg:flex-col lg:items-start lg:justify-normal lg:min-h-[240px] lg:border-t-0 lg:border-r lg:pt-0 lg:pr-8">
          <Link
            href="/"
            className="rounded-sm opacity-65 transition-opacity hover:opacity-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#2B1B1C]"
          >
            <Image
              src="/WCD-Brand-Assets/WCD-Wordmark-dark.svg"
              alt="World Class Design home"
              width={144}
              height={50}
              className="h-10 w-auto grayscale"
            />
          </Link>
          <p className="mt-0 text-xs leading-relaxed text-[#737373] lg:mt-5">
            © {new Date().getFullYear()} World Class Design
          </p>
        </div>

        <nav
          aria-label="Footer"
          className="grid grid-cols-2 gap-x-6 gap-y-9 sm:grid-cols-3 xl:grid-cols-[1fr_1fr_1.2fr_1.5fr_1fr] xl:gap-x-6"
        >
          {FOOTER_GROUPS.map(({ title, links }) => (
            <div
              key={title}
              className={
                title === "Company"
                  ? "order-last col-span-2 sm:col-span-3 xl:order-none xl:col-span-1"
                  : undefined
              }
            >
              <h2 className="mb-3 text-sm font-medium text-[#424242]">
                {title}
              </h2>
              <ul
                className={
                  title === "Company"
                    ? "flex flex-wrap gap-x-5 xl:block xl:space-y-1"
                    : "space-y-1"
                }
              >
                {links.map(({ label, href }) => {
                  const external = href.startsWith("https://");
                  if (href === COOKIE_SETTINGS)
                    return (
                      <li key={label}>
                        <button
                          type="button"
                          className={`cky-banner-element ${linkClass}`}
                        >
                          {label}
                        </button>
                      </li>
                    );
                  return (
                    <li key={label}>
                      <Link
                        href={href}
                        target={external ? "_blank" : undefined}
                        rel={external ? "noopener noreferrer" : undefined}
                        className={linkClass}
                      >
                        {label}
                      </Link>
                    </li>
                  );
                })}
              </ul>
              {title === "Company" && (
                <section aria-labelledby="footer-offices" className="mt-5">
                  <h3 id="footer-offices" className="sr-only">
                    Offices
                  </h3>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-5 xl:grid-cols-1">
                    <address className="text-xs not-italic leading-5 text-[#737373]">
                      <span className="mb-1 block text-[13px] font-medium text-[#424242]">
                        Tallinn
                      </span>
                      <span className="block">Sepapaja tn 6</span>
                      <span className="block">15551, Estonia</span>
                    </address>
                    <address className="text-xs not-italic leading-5 text-[#737373]">
                      <span className="mb-1 block text-[13px] font-medium text-[#424242]">
                        Paris
                      </span>
                      <span className="block">Station F</span>
                      <span className="block">5 Parv. Alan Turing</span>
                      <span className="block">75013, France</span>
                    </address>
                  </div>
                </section>
              )}
            </div>
          ))}
        </nav>
      </div>
    </div>
  </footer>
);

export default SiteFooter;
